import React, {
  Suspense,
  lazy,
} from 'react';
import Page from '@boschebike/ebds-react/dist-esm/components/Page.js';
import {
  componentMap,
  getReactComponentFromApiData,
} from './mapper/componentMapper.js';
import { BrowserHeader } from './react/Header.js';
import { DodoStoreProvider } from './react/DodoStoreProvider.js';
import { ModalSheetProvider } from './react/ModalSheetProvider.js';
import { ToastProvider } from './react/ToastProvider.js';

const Footer = lazy(() => import('./react/Footer.js'));

const PreloadStyle = ({ href }) => {
  // React@18 doesn't support `onLoad` event on link tag.
  // For now we add it synchronously.
  return (
    <link
      rel='stylesheet'
      href={href}
    />
  );
};

const CMSComponents = ({ apiData }) => {
  if (!apiData) {
    return null;
  }

  const map = getReactComponentFromApiData(apiData);

  return map;
};

export const App = ({
  apiData,
  navigation,
  footer,
  userData,
  meta,
  quickLinks,
  withSearch = false,
  withLightFooter = false,
  document,
}) =>
  // Suspense limits hydration error impact. If it happens it will only rerender impacted piece.
  // Removing it would cause e.g. Dashboard to load in inconsistent state when SSR error happens.
  (
    <html className="ebds" lang={document.lang}>
      <head>
        <title>{`${document.title} | ${document.meta.title}` }</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <meta charSet="UTF-8" />
        <meta
          name="robots"
          content={
            document.meta.allowRobots ? 'index,follow' : 'noindex,nofollow'
          }
        />
        <link rel="stylesheet" href={document.appBundle.blockingCSS} />
        {
          document.appBundle.extraCSS ? (
            <PreloadStyle href={document.appBundle.extraCSS} />
          ) : null
        }
        {
          document.appBundle.nonBlockingCSS ? (
            <PreloadStyle href={document.appBundle.nonBlockingCSS} />
          ) : null
        }
        {
          document.appBundle.faviconICO ? (
            <link
              rel="icon"
              type="image/x-icon"
              href={document.appBundle.faviconICO}
            />
          ) : null
        }
        {
          document.appBundle.faviconICO16 ? (
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href={document.appBundle.faviconICO16}
            />
          ) : null
        }
        {
          document.appBundle.faviconICO32 ? (
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href={document.appBundle.faviconICO32}
            />
          ) : null
        }
      </head>
      <body className="ebds">
        <div id="root">
          <DodoStoreProvider>
            <Page
              header={(
                <Suspense fallback={null}>
                  <BrowserHeader
                    navigation={navigation}
                    userData={userData}
                    quickLinks={quickLinks}
                    withSearch={withSearch}
                    meta={meta}
                  />
                </Suspense>
              )}
              footer={(
                <Suspense fallback={null}>
                  <Footer
                    footer={footer}
                    meta={meta}
                    userData={userData}
                    withLightFooter={withLightFooter}
                  />
                </Suspense>
              )}
            >
              <CMSComponents apiData={apiData} />
            </Page>
            <ModalSheetProvider />
            <ToastProvider />
          </DodoStoreProvider>
        </div>
      </body>
    </html>
  );

export const makeAppWithCustomComponents = (customMap) => {
  customMap.forEach((Component, name) => {
    componentMap.set(name, Component);
  });

  return App;
};
